import { Flex, Tag, Typography } from 'antd';
import { ArrowRightOutlined, ExportOutlined } from '@ant-design/icons';

import Accordion from 'components/Accordion';
import TaskInfo from 'components/TaskInfo';

import { withInvoiceCollection } from './withInvoiceCollection';
import { type PaymentNote, withPaymentNotes } from './withPaymentNote';

import {
  formatNumberWithComma,
  renderDate,
  renderInfoValue,
} from 'utils/helpers';
import { useNavigate } from 'react-router-dom';

export interface Invoice {
  invoiceId: string;
  invoiceDate: string;
  invoiceNumber: string;
  amount: number;
  balance: number;
  dueDate: string;
  country: string;
  currencyCode: string;
  farmer: {
    id: string;
    name: string;
  };
  farmerGroup: {
    id: string;
    name: string;
  };
  status: string;
  seasonType: {
    id: string;
    seasonCode: string;
  };
}

const statusMap = {
  partially_paid: {
    label: 'Partial',
    bg: '#FF9900',
    text: '#6B4000',
  },
  paid: {
    label: 'Paid',
    bg: '#339900',
    text: '#FBFBFB',
  },
  unpaid: {
    label: 'Unpaid',
    bg: '#D6D6D6',
    text: '#545454',
  },
  overdue: {
    label: 'Overdue',
    bg: '#FF0000',
    text: '#FBFBFB',
  },
  sent: {
    label: 'Unpaid',
    bg: '#D6D6D6',
    text: '#545454',
  },
};

const BaseInvoice = ({
  invoice,
  paidAt,
  isHoldingCash,
  onHoldingCashClick,
  paymentNotes,
}: {
  invoice: Invoice;
  paidAt?: string | undefined;
  isHoldingCash?: boolean;
  onHoldingCashClick?: () => void;
  paymentNotes?: PaymentNote[];
}) => {
  const renderHeaderContent = () => {
    const properties =
      statusMap?.[invoice?.status as keyof typeof statusMap] ||
      statusMap?.unpaid;
    return (
      <Flex justify="space-between" align="center">
        <Flex>
          <Tag
            color={properties?.bg}
            style={{ color: properties?.text, borderRadius: 20 }}
          >
            {properties?.label}
          </Tag>

          {isHoldingCash && (
            <Tag color="#bfff01" style={{ color: '#000000', borderRadius: 20 }}>
              Holding Cash <ArrowRightOutlined onClick={onHoldingCashClick} />
            </Tag>
          )}
        </Flex>

        {['partially_paid', 'paid']?.includes(invoice?.status) && (
          <Typography.Text
            type="secondary"
            className="info-label"
            style={{ marginRight: -24 }}
          >
            {' '}
            Paid on: {renderDate(paidAt)}
          </Typography.Text>
        )}

        {['unpaid', 'sent', 'overdue']?.includes(invoice?.status) && (
          <Typography.Text
            type="secondary"
            className="info-label"
            style={{ marginRight: -24 }}
          >
            {' '}
            Due on: {renderDate(invoice?.dueDate)}
          </Typography.Text>
        )}
      </Flex>
    );
  };

  return (
    <Accordion
      defaultActiveKey={'0'}
      key={invoice?.invoiceId}
      title={invoice?.farmer?.name}
      titleClassName={'invoice-accordion-title'}
      headerContent={renderHeaderContent()}
    >
      <Flex wrap="wrap" style={{ rowGap: 10, marginLeft: -10 }}>
        <TaskInfo header="Farmer Group" value={invoice?.farmerGroup?.name} />

        <TaskInfo header="Invoice Number" value={invoice?.invoiceNumber} />

        <TaskInfo
          header="Paid Amount"
          value={renderInfoValue(
            `${invoice?.currencyCode} ${formatNumberWithComma(invoice?.amount - invoice?.balance)}`,
          )}
        />

        <TaskInfo header="Season" value={invoice?.seasonType?.seasonCode} />

        {paymentNotes?.map(
          (note, index) =>
            note?.attachmentUrl && (
              <a
                key={note?.id}
                target="_blank"
                rel="noreferrer noopener"
                href={note?.attachmentUrl}
                className="px-2 ml-1 mt-2 link w-100"
              >
                View Payment Note{' '}
                {paymentNotes?.length > 1 ? `${index + 1}` : ''}{' '}
                <ExportOutlined className="ml-2" />
              </a>
            ),
        )}
      </Flex>
    </Accordion>
  );
};

const EnhancedInvoice = withPaymentNotes(withInvoiceCollection(BaseInvoice));

const FarmerInvoice = ({ invoice }: { invoice: Invoice }) => {
  const navigate = useNavigate();

  return (
    <EnhancedInvoice
      invoice={invoice}
      onHoldingCashClick={() =>
        navigate(`/collections/cash?invoiceId=${invoice?.invoiceNumber}`)
      }
    />
  );
};

export default FarmerInvoice;
